import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Map as MapIcon, CheckCircle2, ShieldAlert, BadgeCheck, ArrowRight } from "lucide-react";
import { AskAI } from "@/components/AskAI";
import { NewsSection } from "@/components/NewsSection";
import { UKBackdrop } from "@/components/UKBackdrop";
import { LiveDroneCounter } from "@/components/LiveDroneCounter";
import { CITIES } from "@/data/cities";
import { STATS, WEIGHT_RULES, ID_FACTS, KEY_FACTS } from "@/data/knowledge";
import { GUIDES } from "@/data/guides";
import { GuideCard } from "@/pages/GuidesIndex";
import { LiveMapButton } from "@/components/LiveMapButton";

const NAV_LINKS = [
  { href: "#rules", label: "Rules" },
  { href: "#ids", label: "Registration" },
  { href: "#facts", label: "Key Facts" },
  { href: "#news", label: "News" },
  { href: "#cities", label: "City Guides" },
];

const Nav = () => {
  const [active, setActive] = useState(null);
  return (
    <nav data-testid="home-nav" className="sticky top-0 z-50 bg-black/70 backdrop-blur-2xl border-b border-white/10">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center flex-nowrap gap-3">
        <Link
          to="/"
          data-testid="nav-brand"
          onClick={() => {
            setActive(null);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-2 font-heading font-bold text-lg text-white hover:opacity-80 transition-opacity shrink-0"
        >
          <img src="/logo.png" alt="wherecaniflymydrone logo" data-testid="nav-logo" className="h-7 w-auto" />
          <span>wherecanifly<span className="text-[#2ec4b6]">mydrone</span></span>
        </Link>
        <div className="hidden lg:flex items-center gap-1 text-sm font-medium whitespace-nowrap">
          <button
            data-testid="nav-link-home"
            onClick={() => {
              setActive(null);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className={`px-2.5 py-1.5 rounded-full transition-colors ${
              active === null ? "bg-[#2ec4b6] text-black font-semibold" : "text-zinc-300 hover:text-[#2ec4b6]"
            }`}
          >
            Home
          </button>
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
              onClick={() => setActive(l.href)}
              className={`px-2.5 py-1.5 rounded-full transition-colors ${
                active === l.href
                  ? "bg-[#2ec4b6] text-black font-semibold"
                  : "text-zinc-300 hover:text-[#2ec4b6]"
              }`}
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/guides"
            data-testid="nav-link-guides"
            className="px-2.5 py-1.5 rounded-full text-zinc-300 hover:text-[#2ec4b6] transition-colors"
          >
            Guides
          </Link>
        </div>
        <LiveMapButton testId="nav-open-map" size="md" label="Open Live Map" />
      </div>
    </nav>
  );
};

const MapPreviewCard = () => (
  <Link
    to="/map"
    data-testid="hero-map-preview"
    className="group relative block rounded-3xl overflow-hidden border border-white/10 hover:border-[#2ec4b6]/60 transition-colors"
  >
    <img
      src="/map-preview.jpg"
      alt="Live UK drone airspace map with 1,079 official NATS zones"
      className="w-full h-52 object-cover object-center group-hover:scale-[1.03] transition-transform duration-700"
      loading="lazy"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/90 via-[#0a0a0a]/20 to-transparent" />
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
      <span className="live-map-btn inline-flex items-center gap-2 bg-[#2ec4b6] text-black font-heading font-semibold text-sm px-6 py-3 rounded-full group-hover:bg-[#3dd8ca] transition-colors">
        <MapIcon size={16} /> Check where I can fly <ArrowRight size={15} />
      </span>
    </div>
    <p className="absolute bottom-3 inset-x-0 text-center text-[11px] text-zinc-300 font-medium tracking-wide">
      1,079 live NATS zones · FRZs, danger areas &amp; hot spots · updated daily
    </p>
  </Link>
);

const Hero = () => (
  <header className="relative">
    <div className="relative z-10 max-w-6xl mx-auto px-6 pt-14 pb-10 grid lg:grid-cols-2 gap-10 items-start">
    <div className="pt-4">
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#2ec4b6] mb-4">UK CAA · CAP 722 · Official NATS data</p>
      <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
        Everything you need to fly your drone <span className="text-[#2ec4b6]">legally</span> in the UK
      </h1>
      <p className="text-base leading-relaxed text-zinc-300 mt-5 max-w-xl">
        Ask <span className="text-white font-semibold">Maverick</span> — our CAP 722-trained AI — anything: laws, registration, gear,
        technique. Then check any location on the live map with 1,079 official
        NATS airspace zones, updated every day.
      </p>
      <div className="flex flex-wrap gap-3 mt-7">
        <LiveMapButton testId="hero-open-map" size="lg" label="Check where I can fly" showArrow />
        <a
          href="#rules"
          className="inline-flex items-center gap-2 border border-white/20 text-zinc-100 font-heading font-semibold text-sm px-6 py-3.5 rounded-full hover:border-[#2ec4b6]/60 hover:text-[#2ec4b6] transition-colors"
        >
          Read the rules
        </a>
      </div>
      <LiveDroneCounter />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8">
        {STATS.map((s) => (
          <div key={s.value} data-testid="stat-card">
            <p className="font-heading text-2xl sm:text-3xl font-bold text-white">{s.value}</p>
            <p className="text-xs text-zinc-400 mt-1 leading-snug">{s.label}</p>
          </div>
        ))}
      </div>
    </div>
    <div className="flex flex-col gap-6">
      <AskAI />
      <MapPreviewCard />
    </div>
    </div>
  </header>
);

const RulesSection = () => (
  <section id="rules" data-testid="rules-section" className="max-w-6xl mx-auto px-6 py-16">
    <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#2ec4b6] mb-2">The Open Category, in full detail</p>
    <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-8">UK drone rules by weight class</h2>
    <div className="grid md:grid-cols-3 gap-5">
      {WEIGHT_RULES.map((w) => (
        <div key={w.id} data-testid={`rules-card-${w.id}`} className="bg-white/[0.03] border border-white/10 rounded-3xl p-6 flex flex-col">
          <p className="font-heading text-2xl font-bold" style={{ color: w.color }}>{w.title}</p>
          <p className="text-xs text-zinc-400 mt-1 mb-5">{w.badge}</p>
          <ul className="space-y-3">
            {w.rules.map((r) => (
              <li key={r} className="flex gap-2.5 text-sm text-zinc-200 leading-relaxed">
                <CheckCircle2 size={15} className="shrink-0 mt-0.5" style={{ color: w.color }} />
                {r}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </section>
);

const IdsSection = () => (
  <section id="ids" data-testid="ids-section" className="max-w-6xl mx-auto px-6 py-16">
    <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#2ec4b6] mb-2">Registration & penalties</p>
    <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-8">Flyer ID, Operator ID and what happens if you break the rules</h2>
    <div className="grid md:grid-cols-3 gap-5">
      {ID_FACTS.map((f, i) => (
        <div key={f.title} data-testid="id-card" className="bg-white/[0.03] border border-white/10 rounded-3xl p-6">
          <div className="flex items-center gap-2 mb-4">
            {i === 2 ? <ShieldAlert size={18} className="text-[#ff4d4d]" /> : <BadgeCheck size={18} className="text-[#2ec4b6]" />}
            <h3 className="font-heading font-bold text-lg text-white">{f.title}</h3>
            <span className={`ml-auto text-xs font-bold px-2.5 py-1 rounded-full ${i === 2 ? "bg-[#ff4d4d]/15 text-[#ff4d4d]" : "bg-[#2ec4b6]/15 text-[#2ec4b6]"}`}>{f.cost}</span>
          </div>
          <ul className="space-y-2.5">
            {f.items.map((it) => (
              <li key={it} className="text-sm text-zinc-200 leading-relaxed pl-3 border-l-2 border-white/10">{it}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  </section>
);

const FactsSection = () => (
  <section id="facts" data-testid="facts-section" className="max-w-6xl mx-auto px-6 py-16">
    <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#2ec4b6] mb-2">Know before you launch</p>
    <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-8">Essential facts every UK pilot must know</h2>
    <div className="grid sm:grid-cols-2 gap-5">
      {KEY_FACTS.map((f) => (
        <div key={f.title} data-testid="fact-card" className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
          <h3 className="font-heading font-semibold text-base text-white mb-2">{f.title}</h3>
          <p className="text-sm text-zinc-300 leading-relaxed">{f.text}</p>
        </div>
      ))}
    </div>
  </section>
);

const GuidesSection = () => (
  <section id="guides" data-testid="guides-section" className="max-w-6xl mx-auto px-6 py-16">
    <div className="flex items-end justify-between gap-4 mb-8">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#2ec4b6] mb-2">In-depth guides</p>
        <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Answers to the questions pilots actually ask</h2>
      </div>
      <Link to="/guides" data-testid="all-guides-link" className="hidden sm:inline-flex shrink-0 text-sm text-[#2ec4b6] hover:underline font-heading font-semibold">
        All guides →
      </Link>
    </div>
    <div className="grid md:grid-cols-3 gap-5">
      {GUIDES.slice(0, 3).map((g) => (
        <GuideCard key={g.slug} guide={g} compact />
      ))}
    </div>
    <Link to="/guides" className="sm:hidden inline-block mt-6 text-sm text-[#2ec4b6] font-heading font-semibold">All guides →</Link>
  </section>
);

const CitiesSection = () => (
  <section id="cities" data-testid="cities-section" className="max-w-6xl mx-auto px-6 py-16">
    <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#2ec4b6] mb-2">Regional guides</p>
    <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-8">Can I fly a drone in…</h2>
    <div className="flex flex-wrap gap-3">
      {CITIES.map((c) => (
        <Link
          key={c.slug}
          to={`/can-i-fly-a-drone-in-${c.slug}`}
          data-testid={`home-city-${c.slug}`}
          className="text-sm px-5 py-2.5 rounded-full border border-white/15 text-zinc-100 hover:border-[#2ec4b6]/60 hover:text-[#2ec4b6] transition-colors"
        >
          {c.name}
        </Link>
      ))}
    </div>
  </section>
);

const Footer = () => (
  <footer className="border-t border-white/10 mt-8">
    <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
      <div className="flex items-center gap-2.5">
        <img src="/logo.png" alt="wherecaniflymydrone logo" data-testid="footer-logo" className="h-7 w-auto" />
        <p className="font-heading font-bold text-sm text-white">
          wherecanifly<span className="text-[#2ec4b6]">mydrone</span>
        </p>
      </div>
      <p className="text-xs text-zinc-400 sm:ml-auto max-w-md leading-relaxed">
        Airspace data from official NATS AIP digital datasets, updated daily. Guidance only — always check
        NOTAMs and confirm with the CAA Drone Code before flying.
      </p>
    </div>
  </footer>
);

export default function Home() {
  useEffect(() => {
    document.title = "Where Can I Fly My Drone? | UK Drone Rules, AI Assistant, News & Live Map";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="home-page" className="min-h-screen bg-[#0a0a0a] text-zinc-100">
      <UKBackdrop />
      <div className="relative z-10">
        <Nav />
        <Hero />
        <RulesSection />
        <IdsSection />
        <FactsSection />
        <GuidesSection />
        <NewsSection />
        <CitiesSection />
        <Footer />
      </div>
    </div>
  );
}
